import {
  ApiRequestConfig,
  ApiRequestInterface,
  DeleteParameter,
  ErrorResponse,
  GetParameter,
  PatchParameter,
  PostParameter,
  PutParameter,
} from "./ApiRequest.d";
import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from "axios";
import { AuthUsecase } from "../../usecases/Auth/usecase";

interface GetListResponse<T> {
  Items: T[];
}

export class ApiRequest<T> implements ApiRequestInterface {
  instance: AxiosInstance;
  configure: ApiRequestConfig;

  constructor(
    requestConfig: AxiosRequestConfig,
    extendConfig: ApiRequestConfig,
  ) {
    this.instance = axios.create(requestConfig);
    this.configure = extendConfig;
    this.instance.interceptors.request.use(async (config) => {
      if (this.configure.hasSendToken) {
        try {
          const authUsecase = new AuthUsecase();
          const cognitoUser = await authUsecase.currentAuthenticatedUser();
          if (cognitoUser?.signInUserSession) {
            config.headers.Authorization = `Bearer ${cognitoUser.signInUserSession.idToken.jwtToken}`;
          }
        } catch (error) {
          if (error !== "The user is not authenticated") {
            console.error(error);
          }
        }
      }
      return config;
    });
  }

  async handleErrorAsync<T>(handler: () => Promise<T>) {
    try {
      return await handler();
    } catch (error) {
      const axiosError = error as AxiosError<ErrorResponse>;
      if (axiosError.isAxiosError) {
        throw axiosError.response?.data || axiosError;
      }
      throw error;
    }
  }

  async get({ url }: GetParameter) {
    return await this.handleErrorAsync(() => this.instance.get<T>(url));
  }
  async getList({ url }: GetParameter) {
    return await this.handleErrorAsync(() =>
      this.instance.get<GetListResponse<T>>(url),
    );
  }
  async post<K = unknown>({ url, data, config }: PostParameter) {
    return await this.handleErrorAsync(() =>
      this.instance.post<K>(url, data, config),
    );
  }
  async patch<K = unknown>({ url, data }: PatchParameter) {
    return await this.handleErrorAsync(() => this.instance.patch<K>(url, data));
  }
  async put<K = unknown>({ url, data }: PutParameter) {
    return await this.handleErrorAsync(() => this.instance.put<K>(url, data));
  }
  async delete({ url }: DeleteParameter) {
    return await this.handleErrorAsync(() => this.instance.delete(url));
  }
}
